import { Button } from '@equitymultiple/react-eui';
import { GatsbyImage } from 'gatsby-plugin-image';
import React, { LegacyRef, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { Routes } from '../../../../constants';
import { getCookie } from '../../../../utils';
import SvgProjector from '../../../SvgProjector/SvgProjector';
import {
  ExpandedKegDataProps,
  InvestmentType,
} from '../../expandedKegResources';
import * as styles from './DesktopExpandedKeg.module.scss';

const DesktopExpandedKeg: React.FC<
  Omit<ExpandedKegDataProps, 'mobileDescription'>
> = ({
  investmentType,
  kegIconPath,
  subHeader,
  mainImage,
  description,
  listItems,
  mainImageDescription,
}) => {
  const { ref, inView } = useInView({
    threshold: 1,
  });

  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    const userCookie = getCookie('user');

    if (userCookie) {
      setIsActive(true);
    }
  }, []);

  return (
    <div
      ref={ref as LegacyRef<HTMLDivElement>}
      className={`${styles.desktopSingleExpandedKeg} ${
        inView && styles.active
      }`}
    >
      <div className={styles.leftTabContainer}>
        <div
          className={`${styles.tab} ${styles.leftTab} ${
            styles[investmentType as InvestmentType]
          } ${inView ? styles.active : ''}`}
        />
      </div>

      <div className={`${styles.largeTextContainer}`}>
        <span
          className={`${styles.largeText} ${
            styles[investmentType as InvestmentType]
          } ${inView ? styles.active : ''} heading1 expressive`}
        >
          {investmentType.charAt(0).toUpperCase() + investmentType.slice(1)}
        </span>
      </div>

      <div className={`container ${styles.mainContent}`}>
        <div className="row">
          <div className="col-sm-6">
            <div className={styles.iconHeaderContainer}>
              <h2 className="heading2 expressive">
                {investmentType.charAt(0).toUpperCase() +
                  investmentType.slice(1)}
              </h2>
              <SvgProjector fileUrl={kegIconPath} />
            </div>
            <h6 className={`${styles.expandedKegSubHeader} heading6`}>
              {subHeader}
            </h6>

            {description && <p>{description}</p>}
            {investmentType !== 'keep' && listItems && (
              <>
                <p>Types of investments</p>
                <ul>
                  {listItems.map((listItem) => (
                    <li className={styles.listItem} key={listItem}>
                      {listItem}
                    </li>
                  ))}
                </ul>
              </>
            )}
            <Button
              href={isActive ? Routes.appOfferingsPage.url : Routes.signUp.url}
              className={`${styles.button} ${inView ? styles.active : ''}`}
              variant="outlined"
            >
              Unlock Live Investments
            </Button>
          </div>
          <div className="col-sm-6">
            <div className={styles.mainImageContainer}>
              <span className={`${styles.imageHeader} overline`}>
                Representative Investment
              </span>

              <a
                href={mainImageDescription.offeringLink}
                className={styles.mainImageDescriptionLink}
              >
                <GatsbyImage
                  alt={mainImage.altText}
                  image={mainImage.gatsbyImage}
                  className={styles.gatsbyImage}
                />
                <span
                  className={`${styles.mainImageDescriptionHeader} body1 emphasis`}
                >
                  {mainImageDescription.offeringName}
                </span>
              </a>
              <div className={styles.mainImageDescriptionContainer}>
                <div className={styles.mainImageDescription}>
                  <span className="disclaimer">
                    {mainImageDescription.metric1.label}
                  </span>
                  <span>{mainImageDescription.metric1.value}</span>
                </div>
                <div className={styles.mainImageDescription}>
                  <span className="disclaimer">
                    {mainImageDescription.metric2.label}
                  </span>

                  <span>{mainImageDescription.metric2.value}</span>
                </div>
                <div className={styles.mainImageDescription}>
                  <span className="disclaimer">
                    {mainImageDescription.metric3.label}
                  </span>
                  <span>{mainImageDescription.metric3.value}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.rightTabContainer}>
        <div
          className={`${styles.tab} ${styles.rightTab} ${
            styles[investmentType as InvestmentType]
          } ${inView ? styles.active : ''}`}
        />
      </div>
    </div>
  );
};

export default DesktopExpandedKeg;
